import { memo, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { SupportedLanguage } from '../../i18n';
import ukFlag from './uk-flag.gif';
import caFlag from './ca-flag.png';

type LangEntry = { code: SupportedLanguage; label: string; flag: React.ReactNode };

const LANGUAGES: LangEntry[] = [
  {
    code: 'ca',
    label: 'Català',
    flag: (
      <img
        src={caFlag}
        alt="Català"
        className="w-5 h-5 rounded-full object-cover"
        style={{ width: '20px', height: '20px', borderRadius: '9999px', objectFit: 'cover' }}
      />
    ),
  },
  {
    code: 'es',
    label: 'Español',
    flag: (
      <span
        style={{
          background: 'linear-gradient(180deg, #C60B1E 0%, #C60B1E 25%, #FFC400 25%, #FFC400 75%, #C60B1E 75%, #C60B1E 100%)',
          borderRadius: '9999px',
          width: '20px',
          height: '20px',
          display: 'block',
        }}
      />
    ),
  },
  {
    code: 'en',
    label: 'English',
    flag: (
      <img
        src={ukFlag}
        alt="English"
        className="w-5 h-5 rounded-full object-cover"
        style={{ width: '20px', height: '20px', borderRadius: '9999px', objectFit: 'cover' }}
      />
    ),
  },
];

/**
 * Discreet dropdown language selector for the kiosk interface.
 * Shows a single small trigger (current language flag + chevron) that
 * opens a compact glassmorphism dropdown with all language options.
 *
 * ROOT CAUSE of previous bug (iPad taps not registering):
 *   The parent <header> in KioskPage had `transform: translateZ(0)` (via
 *   kiosk-backdrop-frozen) which created a stacking context. The dropdown's
 *   z-50 was trapped inside that stacking context (which was z-10 in the
 *   parent). The <main> element also had z-10 but came later in DOM order,
 *   so <main> painted ON TOP of the dropdown — intercepting all touch/click
 *   events before they reached the dropdown buttons. No event type change
 *   could fix this because the buttons never received events at all.
 * FIX: Bumped the <header> z-index to z-20 in KioskPage so the header's
 *   stacking context (and its dropdown) paints above <main>.
 *
 * Additional fixes:
 *   - Uses i18nInstance from useTranslation() (not the static import) so the
 *     React hook properly tracks the language change and triggers re-render.
 *   - Adds local useState for current language as a re-render safety net.
 */
export const KioskLanguageSelector = memo(() => {
  const { i18n: i18nInstance } = useTranslation();
  const [currentLang, setCurrentLang] = useState<SupportedLanguage>(
    (i18nInstance.language as SupportedLanguage) || 'es'
  );
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentEntry = LANGUAGES.find((l) => l.code === currentLang) ?? LANGUAGES[0];

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [open]);

  // Sync local state if i18n language changes externally
  useEffect(() => {
    const handler = (lng: string) => {
      if (LANGUAGES.some((l) => l.code === lng)) {
        setCurrentLang(lng as SupportedLanguage);
      }
    };
    i18nInstance.on('languageChanged', handler);
    return () => {
      i18nInstance.off('languageChanged', handler);
    };
  }, [i18nInstance]);

  const handleSelect = (lang: SupportedLanguage) => {
    // Use i18nInstance from useTranslation() — not the static import.
    // This ensures the React hook properly tracks the change and triggers
    // a re-render of this component and all other useTranslation consumers.
    i18nInstance.changeLanguage(lang);
    // Also update local state as a re-render safety net
    setCurrentLang(lang);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger — small, discreet, transparent */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        aria-label={currentEntry.label}
        aria-expanded={open}
        title={currentEntry.label}
        className={`flex items-center gap-1.5 px-2 h-8 rounded-full transition-all active:scale-90 ${
          open
            ? 'bg-slate-800/80 ring-1 ring-indigo-400/50'
            : 'bg-slate-900/40 hover:bg-slate-800/60 ring-1 ring-slate-700/40'
        }`}
      >
        {currentEntry.flag}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-3 w-3 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown panel — glassmorphism, GPU-accelerated open/close
          z-50 is sufficient now that the parent <header> has z-20
          (above <main> z-10) in KioskPage. */}
      {open && (
        <div
          className="absolute right-0 top-full mt-2 min-w-[140px] bg-slate-900/95 border border-slate-700/50 backdrop-blur-xl rounded-xl shadow-2xl overflow-visible z-50"
        >
          {LANGUAGES.map(({ code, label, flag }) => {
            const isActive = currentLang === code;
            return (
              <button
                key={code}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(code);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium transition-colors active:bg-slate-800 first:rounded-t-xl last:rounded-b-xl ${
                  isActive
                    ? 'text-indigo-300 bg-indigo-950/40'
                    : 'text-slate-300 hover:bg-slate-800/60'
                }`}
              >
                {flag}
                <span>{label}</span>
                {isActive && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 ml-auto text-indigo-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
});
KioskLanguageSelector.displayName = 'KioskLanguageSelector';
